import { useState } from "react";
import {
  MessageCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Link2,
} from "lucide-react";
import { toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { useAuth } from "@/shared/hooks/use-auth";
import { LoginForm } from "./login-form";

export function DiscordLinkFlow() {
  const { user, loading: authLoading } = useAuth();
  const params = new URLSearchParams(window.location.search);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-stroke-subtle bg-surface-raised p-8">
        <Loader2 className="size-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (!user) {
    return (
      <LoginForm
        title="Discord 계정 연동"
        description="Discord 계정을 datalab.tools에 연동합니다. 계속하려면 로그인하세요."
        icon={<MessageCircle className="mx-auto mb-2 size-8 text-[#5865F2]" />}
      />
    );
  }

  return <DiscordLinkForm params={params} userEmail={user.email} />;
}

function DiscordLinkForm({
  params,
  userEmail,
}: {
  params: URLSearchParams;
  userEmail?: string;
}) {
  const token = params.get("token");
  const discordId = params.get("discord_id");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token || !discordId) {
    return (
      <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8 text-center">
        <XCircle className="mx-auto mb-3 size-10 text-signal-danger" />
        <h2 className="text-lg font-bold">잘못된 링크</h2>
        <p className="mt-2 text-[13px] text-text-tertiary">
          유효하지 않은 연동 링크입니다. Discord에서 /link-account를 다시
          실행해주세요.
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8 text-center">
        <CheckCircle className="mx-auto mb-3 size-10 text-green-500" />
        <h2 className="text-lg font-bold">연동 완료!</h2>
        <p className="mt-2 text-[13px] text-text-tertiary">
          Discord 계정이 성공적으로 연동되었습니다.
        </p>
        <a
          href="/discord/settings"
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90"
        >
          설정 대시보드로 이동
        </a>
      </div>
    );
  }

  async function handleLink() {
    setSubmitting(true);
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      if (!accessToken)
        throw new Error("세션이 만료되었습니다. 다시 로그인해주세요.");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/discord/auth/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, supabaseToken: accessToken }),
      });

      if (!res.ok) {
        const body = await res
          .json()
          .catch(() => ({ error: "연동에 실패했습니다" }));
        throw new Error(
          (body as { error?: string }).error ?? "연동에 실패했습니다",
        );
      }

      setDone(true);
      toast.success("Discord 계정이 연동되었습니다!");
    } catch (err: any) {
      setError(err.message || "연동에 실패했습니다");
      toast.error(err.message || "연동에 실패했습니다");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-2xl border border-stroke-subtle bg-surface-raised p-8">
      <div className="mb-6 text-center">
        <MessageCircle className="mx-auto mb-2 size-8 text-[#5865F2]" />
        <h1 className="text-xl font-bold">Discord 계정 연동</h1>
        <p className="mt-1 text-[13px] text-text-tertiary">
          Discord 계정을 datalab.tools에 연동합니다.
        </p>
      </div>

      <div className="mb-4 rounded-lg bg-surface-sunken/50 p-3 text-[13px]">
        <span className="text-text-tertiary">datalab 계정: </span>
        <span className="font-medium">{userEmail}</span>
      </div>

      <div className="mb-4 rounded-lg bg-[#5865F2]/10 p-3 text-[13px]">
        <span className="text-text-tertiary">Discord ID: </span>
        <span className="font-medium font-mono">{discordId}</span>
      </div>

      <div className="mb-6 space-y-2">
        {[
          "Discord 사용자 정보 (이름, 아바타)",
          "키워드 조회 및 대시보드 접근",
          "알림 수신 설정",
        ].map((label) => (
          <div
            key={label}
            className="flex items-center gap-2 rounded-lg border border-stroke-subtle px-3 py-2 text-[13px]"
          >
            <CheckCircle className="size-4 text-[#5865F2]" />
            <span>{label}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-signal-danger/10 p-3 text-[13px] text-signal-danger">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => window.close()}
          disabled={submitting}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-stroke-subtle py-2.5 text-[13px] font-medium text-text-tertiary transition-colors hover:bg-surface-sunken disabled:opacity-50"
        >
          <XCircle className="size-4" />
          취소
        </button>
        <button
          onClick={handleLink}
          disabled={submitting}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#5865F2] py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Link2 className="size-4" />
          )}
          {submitting ? "연동 중..." : "연동하기"}
        </button>
      </div>
    </div>
  );
}
